import React, { useState } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Calendar, Clock, GripVertical, Plus, Edit, Trash2, X, Check, Save } from 'lucide-react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin) + '/api';

export default function ServiceScheduleSection({ schedules, setSchedules }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    day: 'Sunday',
    time: '',
    description: ''
  });

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'First Thursday'];

  const handleReorder = (newOrder) => {
    setSchedules(newOrder);
    setHasChanges(true);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const orderIds = schedules.map(s => s.id);
      await axios.post(`${API}/service-schedules/reorder`, orderIds);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving order:', error);
      alert('Failed to save order');
    }
    setSaving(false);
  };

  const addSchedule = async () => {
    if (!newSchedule.name || !newSchedule.time) {
      alert('Please fill in service name and time');
      return;
    }
    try {
      const response = await axios.post(`${API}/service-schedule`, {
        ...newSchedule,
        display_order: schedules.length
      });
      setSchedules(prev => [...prev, response.data]);
      setNewSchedule({ name: '', day: 'Sunday', time: '', description: '' });
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding schedule:', error);
      alert('Failed to add service schedule');
    }
  };

  const updateSchedule = async () => {
    if (!editingSchedule) return;
    try {
      await axios.put(`${API}/service-schedule/${editingSchedule.id}`, editingSchedule);
      setSchedules(prev => prev.map(s => s.id === editingSchedule.id ? editingSchedule : s));
      setEditingSchedule(null);
    } catch (error) {
      console.error('Error updating schedule:', error);
      alert('Failed to update service schedule');
    }
  };

  const deleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this service schedule?')) return;
    try {
      await axios.delete(`${API}/service-schedule/${scheduleId}`);
      setSchedules(prev => prev.filter(s => s.id !== scheduleId));
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('Failed to delete service schedule');
    }
  };

  const gradients = [
    'from-blue-500 to-cyan-500',
    'from-purple-500 to-pink-500',
    'from-orange-500 to-red-500',
    'from-emerald-500 to-teal-500',
    'from-indigo-500 to-blue-500',
    'from-rose-500 to-pink-500',
    'from-amber-500 to-yellow-500',
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Service Schedule</h2>
          <p className="text-slate-600">Drag to reorder services • Changes auto-save</p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <button
              onClick={saveOrder}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all font-medium shadow-lg"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Order'}
            </button>
          )}
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all font-medium shadow-lg"
          >
            <Plus className="w-4 h-4" />
            Add Service
          </button>
        </div>
      </div>

      {/* Drag and Drop List */}
      <Reorder.Group axis="y" values={schedules} onReorder={handleReorder} className="space-y-3">
        {schedules.map((schedule, index) => (
          <Reorder.Item
            key={schedule.id}
            value={schedule}
            className="cursor-grab active:cursor-grabbing"
          >
            <motion.div
              className={`flex items-center gap-4 p-4 bg-gradient-to-r ${gradients[index % gradients.length]} rounded-2xl shadow-lg text-white`}
              whileHover={{ scale: 1.01 }}
              layout
            >
              {/* Drag Handle */}
              <div className="p-2 bg-white/20 rounded-lg">
                <GripVertical className="w-5 h-5" />
              </div>

              {/* Order Number */}
              <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full font-bold">
                {index + 1}
              </div>

              {/* Service Info */}
              <div className="flex-1">
                <h3 className="font-bold text-lg">{schedule.name}</h3>
                <div className="flex items-center gap-4 text-white/90 text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {schedule.day}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {schedule.time}
                  </span>
                </div>
                {schedule.description && (
                  <p className="text-white/70 text-sm mt-1">{schedule.description}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingSchedule({ ...schedule });
                  }}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSchedule(schedule.id);
                  }}
                  className="p-2 bg-red-500/50 hover:bg-red-500/70 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </Reorder.Item>
        ))}
      </Reorder.Group>

      {schedules.length === 0 && (
        <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No service schedules yet</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-3 text-blue-600 hover:text-blue-700 font-medium"
          >
            Add your first service
          </button>
        </div>
      )}

      {/* Add Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-800">Add New Service</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Service Name *</label>
                  <input
                    type="text"
                    value={newSchedule.name}
                    onChange={(e) => setNewSchedule(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Sunday Morning Service"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Day *</label>
                    <select
                      value={newSchedule.day}
                      onChange={(e) => setNewSchedule(prev => ({ ...prev, day: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none transition-colors"
                    >
                      {days.map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Time *</label>
                    <input
                      type="text"
                      value={newSchedule.time}
                      onChange={(e) => setNewSchedule(prev => ({ ...prev, time: e.target.value }))}
                      placeholder="e.g., 10:00 AM"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
                  <textarea
                    value={newSchedule.description}
                    onChange={(e) => setNewSchedule(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the service"
                    rows={2}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none transition-colors resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-3 border-2 border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addSchedule}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all font-medium"
                  >
                    Add Service
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {editingSchedule && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-800">Edit Service</h3>
                <button
                  onClick={() => setEditingSchedule(null)}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Service Name *</label>
                  <input
                    type="text"
                    value={editingSchedule.name}
                    onChange={(e) => setEditingSchedule(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Day *</label>
                    <select
                      value={editingSchedule.day}
                      onChange={(e) => setEditingSchedule(prev => ({ ...prev, day: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none transition-colors"
                    >
                      {days.map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Time *</label>
                    <input
                      type="text"
                      value={editingSchedule.time}
                      onChange={(e) => setEditingSchedule(prev => ({ ...prev, time: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={editingSchedule.description || ''}
                    onChange={(e) => setEditingSchedule(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none transition-colors resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setEditingSchedule(null)}
                    className="flex-1 px-4 py-3 border-2 border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={updateSchedule}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all font-medium flex items-center justify-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Save Changes
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
